import { CancelStopDialog } from '@folio/funnels/Stop/CancelStopDialog';
import { useCancelStop } from '@folio/funnels/Stop/useCancelStop';
import { remainingCancelWindow, resolveStopBanner } from '@folio/utils/stop-banner';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ClockIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle } from '../../components/ui/alert';
import { StopAlertLayout, StopAlertMessage } from './StopAlertLayout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { formatZonedDateTime } from '../../lib/date-formatting';

/**
 * "Scheduled termination" is derived, not a status: a stop recorded on the policy that has not
 * taken effect yet. Only a stop carrying an executesAt runs on the branch model and can be
 * cancelled — without one the stop is already written to the policy.
 */
export function ScheduledStopAlert({ className }: { className?: string } = {}) {
  const { t } = useTranslation();
  // BASE, not the viewed branch: an echo on a STOP branch is a draft, not a scheduled stop.
  const { policyOnMain: policy } = usePolicyOnDashboard();
  const [open, setOpen] = useState(false);
  const { mayCancel, cancel, isCancelling } = useCancelStop();

  const pending = !!policy.stop && policy.status !== 'POLICY_STOPPED';

  if (!pending || !policy.stop) {
    return null;
  }

  const { stop } = policy;
  const stopDate = policy.stoppedAt ?? stop.stopDate;
  const reason = t([
    `${policy.productId}.stopReasons.${stop.reason}`,
    `stopReasons.${stop.reason}`,
    stop.reason,
  ]);

  const state = resolveStopBanner({
    effectiveAt: stopDate,
    executesAt: stop.executesAt,
    awaitingValidation: !!policy.isStopAwaitingValidation,
  });

  const cancellableUntil = remainingCancelWindow({ state, now: Date.now() });
  const timeLeftToCancel = cancellableUntil
    ? formatDistanceToNow(new Date(cancellableUntil), { locale: fr })
    : undefined;

  const namespace = {
    awaitingValidation: 'stopAwaitingValidation',
    scheduled: 'scheduledStop',
    requested: 'stopRequested',
  }[state.kind];
  const translate = (key: string) =>
    t([`${policy.productId}.${namespace}.${key}`, `${namespace}.${key}`], {
      date: state.effectiveAt
        ? formatZonedDateTime(new Date(state.effectiveAt), policy.timeZone)
        : undefined,
      reason,
    });

  const canCancel = state.kind !== 'requested' && mayCancel;
  const cancelNamespace =
    state.kind === 'awaitingValidation' ? 'stopAwaitingValidation' : 'scheduledStop';

  return (
    <Alert variant="info" className={className}>
      <AlertTitle className="flex items-center justify-between gap-4 pt-1">
        <StopAlertLayout icon={<ClockIcon aria-hidden="true" className="mt-0.5 shrink-0" />}>
          <StopAlertMessage
            lead={translate('lead')}
            body={translate(
              state.kind === 'awaitingValidation' &&
                state.effectiveAt &&
                new Date(state.effectiveAt).getTime() < Date.now()
                ? 'bodyPastDate'
                : 'body',
            )}
            meta={translate(state.effectiveAt ? 'meta' : 'metaNoDate')}
          />
        </StopAlertLayout>
        <span className="flex items-center gap-2">
          {timeLeftToCancel && canCancel && (
            <Badge variant="secondary" className="whitespace-nowrap font-normal">
              {t('scheduledStop.timeLeftToCancel', { duration: timeLeftToCancel })}
            </Badge>
          )}
          {canCancel && (
            <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
              {t(`${cancelNamespace}.cancel`)}
            </Button>
          )}
        </span>
      </AlertTitle>

      <CancelStopDialog
        open={open}
        onOpenChange={setOpen}
        title={t(`${cancelNamespace}.dialog.title`)}
        description={t(`${cancelNamespace}.dialog.description`, {
          date: stopDate ? formatZonedDateTime(new Date(stopDate), policy.timeZone) : '',
          reason,
        })}
        submitLabel={t(`${cancelNamespace}.dialog.submit`)}
        isPending={isCancelling}
        onSubmit={cancel}
      />
    </Alert>
  );
}
